import { ref, watch, computed, onMounted } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'

import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const ASSET_MODEL_LIMIT_STORE_MODULE_NAME = 'app-asset-model-limit'

export default function useAssetModelLimitList() {
  const toast = useToast()
  const refAssetModelLimitListTable = ref(null)

  const tableColumns = [
    { key: 'assetModelName', sortable: false, label: i18n.t('Asset Model') },
    { key: 'alertType', sortable: false, label: i18n.t('Alert Type') },
    { key: 'limitType', sortable: false, label: i18n.t('Limit Type') },
    { key: 'telemetryParameterName', sortable: false, label: i18n.t('Telemetry Parameter') },
    { key: 'value', sortable: false, label: i18n.t('Value') },
    { key: 'actions', label: i18n.t('Actions') },
  ]

  const perPage = ref(10)
  const totalItems = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')

  const alertTypeId = ref(null)
  const limitTypeId = ref(null)
  const assetModelId = ref(null)

  const alertOptions = ref([])
  const limitOptions = ref([])

  const dataMeta = computed(() => {
    const localItemsCount = refAssetModelLimitListTable.value
      ? refAssetModelLimitListTable.value.localItems.length
      : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalItems.value,
    }
  })

  const refetchData = () => {
    if (refAssetModelLimitListTable.value) {
      refAssetModelLimitListTable.value.refresh()
    }
  }

  const fetchSearchAssetModelLimit = (ctx, callback) => {
    store
      .dispatch(`${ASSET_MODEL_LIMIT_STORE_MODULE_NAME}/fetchSearchAssetModelLimit`, {
        search: searchQuery.value || undefined,
        assetModelId: assetModelId.value || undefined,
        limitTypeId: limitTypeId.value || undefined,
        alertTypeId: alertTypeId.value || undefined,
        page: currentPage.value,
        perPage: perPage.value,
      })
      .then(response => {
        const data = response.data?.data ?? response.data ?? []
        const total = response.data?.totalItems ?? (Array.isArray(data) ? data.length : 0)
        callback(Array.isArray(data) ? data : [])
        totalItems.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('Error fetching asset model limits'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        callback([])
      })
  }

  const loadAlertOptions = () => {
    store
      .dispatch(`${ASSET_MODEL_LIMIT_STORE_MODULE_NAME}/fetchAlerts`)
      .then(response => {
        const list = response.data?.data ?? response.data ?? []
        alertOptions.value = [
          { label: i18n.t('All'), value: null },
          ...list.map(item => ({
            label: item.name ?? item.Name,
            value: item.id ?? item.Id,
          })),
        ]
      })
      .catch(() => {
        alertOptions.value = [{ label: i18n.t('All'), value: null }]
      })
  }

  const loadLimitOptions = () => {
    store
      .dispatch(`${ASSET_MODEL_LIMIT_STORE_MODULE_NAME}/fetchLimits`)
      .then(response => {
        const list = response.data?.data ?? response.data ?? []
        limitOptions.value = [
          { label: i18n.t('All'), value: null },
          ...list.map(item => ({
            label: item.name ?? item.Name,
            value: item.id ?? item.Id,
          })),
        ]
      })
      .catch(() => {
        limitOptions.value = [{ label: i18n.t('All'), value: null }]
      })
  }

  watch([currentPage, perPage, searchQuery, alertTypeId, limitTypeId, assetModelId], () => {
    refetchData()
  })

  onMounted(() => {
    loadAlertOptions()
    loadLimitOptions()
  })

  return {
    fetchSearchAssetModelLimit,
    tableColumns,
    perPage,
    currentPage,
    totalItems,
    dataMeta,
    perPageOptions,
    searchQuery,
    refAssetModelLimitListTable,
    refetchData,
    alertTypeId,
    limitTypeId,
    assetModelId,
    alertOptions,
    limitOptions,
  }
}
